import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import supabase from "../supabase";
import { createPortal } from "react-dom";
import CreateProjectModal from "./CreateProjectModal";

const TrashIcon = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M9 3h6m-7 4h8m-9 0h10l-1 14a2 2 0 0 1-2 2H10a2 2 0 0 1-2-2L7 7Z"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M10 11v7M14 11v7"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const DotsIcon = ({ size = 18 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M12 6.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3ZM12 13.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3ZM12 20.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z"
      fill="#111827"
    />
  </svg>
);

export default function ProjectsPage({ profile, organization }) {
  const navigate = useNavigate();

  const [openMenuProjectId, setOpenMenuProjectId] = useState(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const menuRef = useRef(null);
  const { companyId } = useParams();
  const [projects, setProjects] = useState([]);
  const [usersById, setUsersById] = useState({});
  const [articleCounts, setArticleCounts] = useState({});
  const [loading, setLoading] = useState(true);

  const [isProjectModalOpen, setProjectModalOpen] = useState(false);

  const isAdmin = (profile?.role || "").toLowerCase() === "admin";

  const closeMenu = () => setOpenMenuProjectId(null);

  const handleOpenProject = (projectId) => {
    closeMenu();
    navigate(`/organization-portal/${companyId}/projects/${projectId}`);
  };

  const handleRenameProject = async (project) => {
    closeMenu();
    if (!isAdmin) return;

    const nextTitle = prompt("Rename project:", project.title);
    if (!nextTitle || !nextTitle.trim()) return;

    const { error } = await supabase
      .from("projects")
      .update({ title: nextTitle.trim() })
      .eq("id", project.id);

    if (error) {
      console.error(error);
      alert("Failed to rename project.");
      return;
    }

    await load();
  };

  const handleDeleteProject = async (project) => {
    closeMenu();
    if (!isAdmin) return;

    const ok = window.confirm(
      `Delete "${project.title}"? This cannot be undone.`
    );
    if (!ok) return;

    const { error } = await supabase
      .from("projects")
      .delete()
      .eq("id", project.id);

    if (error) {
      console.error(error);
      alert("Failed to delete project.");
      return;
    }

    await load();
  };

  const load = async () => {
    if (!companyId) return;
    setLoading(true);

    try {
      // 1) projects
      const { data: projData, error: projErr } = await supabase
        .from("projects")
        .select(
          "id, title, organization, description, writers, editors, created_at, updated_at"
        )
        .eq("company_id", companyId)
        .order("updated_at", { ascending: false });

      if (projErr) throw projErr;
      const rows = projData || [];
      setProjects(rows);

      // 2) org users (for avatars)
      // ✅ Added avatar_url to the selection
      const { data: userData, error: userErr } = await supabase
        .from("Users")
        .select("id, name, email, role, avatar_url")
        .eq("company_id", companyId);

      if (userErr) throw userErr;

      const map = {};
      (userData || []).forEach((u) => (map[u.id] = u));
      setUsersById(map);

      // 3) article counts per project
      const ids = rows.map((r) => r.id).filter(Boolean);
      if (ids.length) {
        const { data: artData, error: artErr } = await supabase
          .from("articles")
          .select("id, project_id")
          .in("project_id", ids);

        if (artErr) {
          console.warn("[ProjectsPage] article count error:", artErr);
          setArticleCounts({});
        } else {
          const counts = {};
          (artData || []).forEach((a) => {
            const pid = a.project_id;
            if (!pid) return;
            counts[pid] = (counts[pid] || 0) + 1;
          });
          setArticleCounts(counts);
        }
      } else {
        setArticleCounts({});
      }
    } catch (e) {
      console.error("[ProjectsPage] load error:", e);
      alert("Failed to load projects.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [companyId]);

  useEffect(() => {
    if (!openMenuProjectId) return;

    const onMouseDown = (e) => {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(e.target)) closeMenu();
    };

    const onKeyDown = (e) => {
      if (e.key === "Escape") closeMenu();
    };

    window.addEventListener("mousedown", onMouseDown);
    window.addEventListener("keydown", onKeyDown);

    return () => {
      window.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [openMenuProjectId]);

  const initials = (nameOrEmail = "?") =>
    nameOrEmail
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((s) => s[0]?.toUpperCase())
      .join("") || "?";

  const formatDate = (d) => {
    if (!d) return "";
    const dt = new Date(d);
    return dt.toLocaleDateString(undefined, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  const renderPeople = (ids) => {
    const list = (ids || []).filter(Boolean);
    const shown = list.slice(0, 3);
    const extra = Math.max(0, list.length - shown.length);

    return (
      <div style={styles.avatarStack}>
        {shown.map((id) => {
          const u = usersById[id];
          const label = u?.name || u?.email || "?";
          // ✅ Check for avatar_url
          const avatarUrl = u?.avatar_url;

          return (
            <div key={id} title={label} style={styles.avatar}>
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt={label}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                initials(label)
              )}
            </div>
          );
        })}
        {extra > 0 && <div style={styles.avatarMore}>+{extra}</div>}
      </div>
    );
  };

  const handleCreateProject = async (formData) => {
    // ✅ Now extracting org_id and org_name from the modal's payload
    const { title, org_id, org_name, description, writer_ids, editor_ids } =
      formData;

    const payload = {
      title,
      organization: org_name,
      description: description || null,
      // ✅ This ensures it shows up in the chosen organization's portal
      company_id: org_id || null,
      created_by: profile?.id,
      writers: writer_ids?.length ? writer_ids : null,
      editors: editor_ids?.length ? editor_ids : null,
    };

    const { error } = await supabase.from("projects").insert(payload);
    if (error) throw error;

    setProjectModalOpen(false);

    // Only reload the table if the project was assigned to the portal we are currently looking at
    if (org_id === companyId) {
      await load();
    } else {
      alert(`Project successfully assigned to ${org_name}.`);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.headerRow}>
        <div>
          <h1 style={styles.title}>Projects</h1>
        </div>

        <button
          style={styles.newButton}
          onClick={() => {
            if (!isAdmin) return alert("Only Admins can create projects.");
            setProjectModalOpen(true);
          }}
        >
          + New Project
        </button>
      </div>

      <div style={styles.card}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Project Name</th>
              <th style={styles.th}>Organization</th>
              <th style={styles.th}>Writers</th>
              <th style={styles.th}>Editors</th>
              <th style={styles.th}>Article Count</th>
              <th style={styles.th}>Last Updated</th>
              <th style={{ ...styles.th, textAlign: "right" }}>Actions</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} style={styles.emptyState}>
                  Loading…
                </td>
              </tr>
            ) : projects.length === 0 ? (
              <tr>
                <td colSpan={7} style={styles.emptyState}>
                  No projects found.
                </td>
              </tr>
            ) : (
              projects.map((p) => (
                <tr key={p.id} style={styles.tr}>
                  <td style={styles.tdStrong}>
                    <button
                      type="button"
                      onClick={() => handleOpenProject(p.id)}
                      style={{
                        background: "transparent",
                        border: "none",
                        padding: 0,
                        margin: 0,
                        cursor: "pointer",
                        textAlign: "left",
                        font: "inherit",
                        color: "inherit",
                        fontWeight: "inherit",
                        width: "100%",
                      }}
                      title="Open project"
                    >
                      {p.title}
                    </button>
                  </td>

                  <td style={styles.td}>{p.organization}</td>
                  <td style={styles.td}>{renderPeople(p.writers)}</td>
                  <td style={styles.td}>{renderPeople(p.editors)}</td>
                  <td style={styles.td}>{articleCounts[p.id] || 0}</td>
                  <td style={styles.td}>
                    {formatDate(p.updated_at || p.created_at)}
                  </td>
                  <td style={{ ...styles.td, textAlign: "right" }}>
                    <button
                      style={styles.iconBtn}
                      title="Actions"
                      onClick={(e) => {
                        e.stopPropagation();

                        const rect = e.currentTarget.getBoundingClientRect();

                        const MENU_W = 180;
                        const left = Math.max(12, rect.right - MENU_W);
                        const top = rect.bottom + 8;

                        setMenuPos({ top, left });
                        setOpenMenuProjectId((prev) =>
                          prev === p.id ? null : p.id
                        );
                      }}
                    >
                      <DotsIcon />
                    </button>

                    {openMenuProjectId === p.id &&
                      createPortal(
                        <div
                          ref={menuRef}
                          style={{
                            ...styles.menu,
                            top: menuPos.top,
                            left: menuPos.left,
                          }}
                        >
                          <button
                            style={styles.menuItem}
                            onClick={() => handleOpenProject(p.id)}
                          >
                            Open
                          </button>

                          <button
                            style={{
                              ...styles.menuItem,
                              ...(isAdmin ? {} : styles.menuItemDisabled),
                            }}
                            disabled={!isAdmin}
                            onClick={() => handleRenameProject(p)}
                          >
                            Rename
                          </button>

                          <button
                            style={{
                              ...styles.menuItem,
                              ...(isAdmin
                                ? styles.menuItemDanger
                                : styles.menuItemDisabled),
                            }}
                            disabled={!isAdmin}
                            onClick={() => handleDeleteProject(p)}
                          >
                            Delete
                          </button>
                        </div>,
                        document.body
                      )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <CreateProjectModal
        isOpen={isProjectModalOpen}
        onClose={() => setProjectModalOpen(false)}
        onCreate={handleCreateProject}
        orgId={companyId}
        currentUserRole={profile?.role}
        lockOrg={!organization?.ai_enabled}
      />
    </div>
  );
}

const styles = {
  page: {
    padding: "28px 28px 40px",
    background: "white",
    minHeight: "100vh",
  },
  headerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 18,
  },
  breadcrumbs: { fontSize: 13, color: "#6b7280", marginBottom: 6 },
  title: { fontSize: 24, fontWeight: 700, color: "#111827", margin: 0 },
  newButton: {
    background: "#00876e",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    padding: "10px 14px",
    fontWeight: 700,
    cursor: "pointer",
  },
  card: {
    background: "white",
    borderRadius: 14,
    border: "1px solid #e5e7eb",
    overflow: "hidden",
  },
  table: { width: "100%", borderCollapse: "collapse" },
  th: {
    textAlign: "left",
    fontSize: 12,
    fontWeight: 700,
    color: "#6b7280",
    padding: "14px 18px",
    borderBottom: "1px solid #e5e7eb",
    background: "#ffffff",
    whiteSpace: "nowrap",
  },
  tr: { borderBottom: "1px solid #eef2f7" },
  td: {
    padding: "14px 18px",
    fontSize: 14,
    color: "#111827",
    verticalAlign: "middle",
  },
  tdStrong: {
    padding: "14px 18px",
    fontSize: 14,
    color: "#111827",
    fontWeight: 700,
  },
  emptyState: {
    padding: 18,
    color: "#6b7280",
    fontSize: 14,
    textAlign: "center",
  },
  avatarStack: { display: "inline-flex", alignItems: "center" },
  avatar: {
    width: 26,
    height: 26,
    borderRadius: "50%",
    background: "#111827",
    color: "#fff",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
    fontSize: 11,
    border: "2px solid #fff",
    marginLeft: -6,
    overflow: "hidden", // ✅ Ensuring image is clipped
  },
  avatarMore: {
    marginLeft: 8,
    fontSize: 12,
    color: "#374151",
    fontWeight: 700,
  },
  iconBtn: {
    border: "1px solid #e5e7eb",
    background: "#fff",
    borderRadius: 10,
    width: 40,
    height: 36,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  menu: {
    position: "fixed",
    zIndex: 999999,
    width: 180,
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    boxShadow: "0 16px 40px rgba(0,0,0,0.12)",
    overflow: "hidden",
    padding: 6,
  },

  menuItem: {
    width: "100%",
    border: "none",
    background: "transparent",
    padding: "10px 12px",
    textAlign: "left",
    cursor: "pointer",
    borderRadius: 10,
    fontWeight: 600,
    fontSize: 14,
    color: "#111827",
  },

  menuItemDanger: {
    color: "#e11d48",
  },

  menuItemDisabled: {
    color: "#9ca3af",
    cursor: "not-allowed",
  },
};
